/**
 * React Flow node positions, per workflow version, in this browser's localStorage.
 *
 * Keyed by the **root** of the version chain rather than by the workflow's own id. Editing a
 * published workflow mints a new draft row with a new id, so an id-keyed record was lost on
 * every fork — the arrangement disappeared at exactly the moment an admin had finished
 * arranging it. One record per chain holds a map per version, and a version with no layout of
 * its own inherits the newest older version that has one.
 *
 * Inheriting is resolved on read rather than copied on fork, deliberately: the fork can happen
 * in a different browser from the one holding the layout, and a read-time fallback covers that
 * while a copy cannot.
 *
 * Still per browser. A teammate opening the same workflow gets auto layout, and clearing site
 * data loses the arrangement. Making it shared means x/y on `workflow_steps` and a rule for
 * whose arrangement wins — see docs/dev/technical-improvements.md.
 */

const STORAGE_KEY_PREFIX = 'maintella:workflowFlowPositions:v2:';

// v1 keyed one record per workflow id, with positions at the top level. Bumping the prefix
// without reading the old key would have thrown away the arrangement of everyone who has
// actually used this feature — a silent regression for exactly the heaviest users. Read as a
// fallback, never written; it can go once the v1 keys have aged out of real browsers.
const LEGACY_KEY_PREFIX = 'maintella:workflowFlowPositions:v1:';

function readLegacyPositions(workflowId) {
  if (workflowId == null || workflowId === '') return null;
  try {
    const raw = window.localStorage.getItem(LEGACY_KEY_PREFIX + workflowId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.positions || typeof parsed.positions !== 'object') return null;
    return Object.keys(parsed.positions).length > 0 ? parsed.positions : null;
  } catch {
    return null;
  }
}

// Older versions are kept so a fork can inherit from them, but not forever: a long-lived
// workflow would otherwise grow one map per version in a store with a few megabytes to spare.
// Newest kept, oldest dropped.
const MAX_VERSIONS_KEPT = 10;

function storageKey(rootId) {
  return `${STORAGE_KEY_PREFIX}${rootId}`;
}

function readRecord(rootId) {
  if (rootId == null || rootId === '') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(rootId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.versions || typeof parsed.versions !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function sanitizePositions(positionsById) {
  const positions = {};
  for (const [id, pos] of Object.entries(positionsById || {})) {
    if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
      positions[id] = { x: pos.x, y: pos.y };
    }
  }
  return positions;
}

/**
 * The layout to start this version from: its own if it has one, otherwise the newest version
 * below it that does.
 *
 * "Below it" and not "any version in the family": after a draft is discarded and the published
 * version forked again, two drafts can share a root, and an abandoned draft's arrangement should
 * not beat the published version's. Version numbers are ordered, so taking the highest that is
 * still lower than this one follows the line of descent without needing the chain itself.
 */
export function loadFlowLayout({ rootId, workflowId, version }) {
  const record = readRecord(rootId);

  const own = record?.versions?.[String(workflowId)];
  if (own?.positions && Object.keys(own.positions).length > 0) {
    return { ...own, inherited: false };
  }

  // This workflow's own v1 record counts as its own layout, not an inherited one: it was saved
  // against this very id.
  const legacy = readLegacyPositions(workflowId);
  if (legacy) return { positions: legacy, version: version ?? null, inherited: false };

  if (!record || !Number.isFinite(version)) return null;

  const inheritable = Object.values(record.versions)
    .filter((entry) => (
      Number.isFinite(entry?.version)
      && entry.version < version
      && entry.positions
      && Object.keys(entry.positions).length > 0
    ))
    .sort((a, b) => b.version - a.version);

  return inheritable.length > 0 ? { ...inheritable[0], inherited: true } : null;
}

export function saveFlowLayout({ rootId, workflowId, version, positionsById, workflowSteps }) {
  if (rootId == null || rootId === '' || workflowId == null || workflowId === '') return;

  const record = readRecord(rootId) || { versions: {} };
  record.versions[String(workflowId)] = {
    version: Number.isFinite(version) ? version : null,
    positions: sanitizePositions(positionsById),
    // The step ids this layout was saved against. Read by `layoutStillFits` so an inherited
    // layout whose steps no longer resemble this version's is not applied; it used to be
    // written on every record and never looked at.
    stepIds: (workflowSteps || []).map((step) => step?.id).filter(Boolean).sort(),
    savedAt: new Date().toISOString(),
  };

  record.versions = pruneOldVersions(record.versions, workflowId);

  try {
    window.localStorage.setItem(storageKey(rootId), JSON.stringify(record));
  } catch (e) {
    console.warn('Could not persist flow layout', e);
  }
}

// Keep the current version and the newest of the rest.
function pruneOldVersions(versions, currentWorkflowId) {
  const entries = Object.entries(versions);
  if (entries.length <= MAX_VERSIONS_KEPT) return versions;

  const current = String(currentWorkflowId);
  const ranked = entries
    .filter(([id]) => id !== current)
    .sort(([, a], [, b]) => (b?.version ?? 0) - (a?.version ?? 0))
    .slice(0, MAX_VERSIONS_KEPT - 1);

  return Object.fromEntries([[current, versions[current]], ...ranked]);
}

/**
 * Does a saved layout still describe something like this version's steps?
 *
 * A version's own layout is always applied — `mergeBaselineNodesWithSaved` places the ids it
 * finds and leaves anything new on the auto layout, which is the behaviour you want while
 * editing. An **inherited** layout is different: if the draft has since been rebuilt, placing a
 * handful of coincidentally-matching ids is worse than laying the whole thing out afresh. So an
 * inherited layout has to cover at least half of the current steps to be worth using.
 */
export function layoutStillFits(saved, workflowSteps) {
  if (!saved?.positions) return false;
  if (!saved.inherited) return true;

  const currentIds = (workflowSteps || []).map((step) => step?.id).filter(Boolean);
  if (currentIds.length === 0) return false;

  const covered = currentIds.filter((id) => saved.positions[id]).length;
  return covered * 2 >= currentIds.length;
}

/**
 * The one way to apply a stored layout to freshly laid-out nodes: look it up by the workflow's
 * chain, discard it if it is an inherited layout that no longer fits, and merge what is left.
 * All three call sites went through `load` + `merge` separately before, which is how one of them
 * would have missed the inheritance rule.
 */
export function applyStoredLayout(baselineNodes, workflow) {
  if (!workflow?.id) return baselineNodes;

  const saved = loadFlowLayout({
    rootId: workflow.root_workflow_id ?? workflow.id,
    workflowId: workflow.id,
    version: workflow.version,
  });

  if (!layoutStillFits(saved, workflow.workflow_steps)) return baselineNodes;

  return mergeBaselineNodesWithSaved(baselineNodes, saved);
}

export function mergeBaselineNodesWithSaved(baselineNodes, savedRecord) {
  if (!savedRecord?.positions) return baselineNodes;
  const saved = savedRecord.positions;
  return baselineNodes.map((node) => {
    const p = saved[node.id];
    if (p && typeof p.x === 'number' && typeof p.y === 'number') {
      return { ...node, position: { x: p.x, y: p.y } };
    }
    return node;
  });
}
